import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DateTime } from '@/components/ui/DateTime'
import { handleApiError } from '@/lib/api/axios'
import { useCan } from '@/hooks/useCan'
import { useUser } from '@/app/store/auth'
import { enrolmentApi, enrolmentKeys, type EnrolmentCode } from '../api'

/**
 * Enrolment codes for one user, for an administrator.
 *
 * Issue (users.enrolment.issue): a reason is asked for, and the code is shown ONCE - it is not
 * stored and cannot be shown again. For an administrator or a recovery manager it does not work
 * until a second administrator approves it (users.enrolment.approve): not the one who issued it,
 * and not the user it is for. The history below shows every code issued, never the code.
 */
export function EnrolmentCodeDialog({ user, open, onClose }: { user: { id: number; name?: string; email: string } | null; open: boolean; onClose: () => void }) {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const me = useUser()
    const mayIssue = useCan('users.enrolment.issue')
    const mayApprove = useCan('users.enrolment.approve')
    const [reason, setReason] = useState('')
    const [issued, setIssued] = useState<{ code: string; record: EnrolmentCode } | null>(null)
    const userId = user?.id ?? 0
    const myId = Number(me?.id ?? 0)

    const codes = useQuery({ queryKey: enrolmentKeys.forUser(userId), queryFn: () => enrolmentApi.listForUser(userId), enabled: open && userId > 0 })
    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: enrolmentKeys.forUser(userId) })
        queryClient.invalidateQueries({ queryKey: enrolmentKeys.pending })
    }
    const issue = useMutation({
        mutationFn: () => enrolmentApi.issue({ userId, reason: reason.trim() }),
        onSuccess: (result) => { setIssued(result); setReason(''); refresh() },
    })
    const approve = useMutation({ mutationFn: (id: number) => enrolmentApi.approve(id), onSuccess: refresh })
    const revoke = useMutation({ mutationFn: (id: number) => enrolmentApi.revoke(id, 'Withdrawn by an administrator'), onSuccess: refresh })
    const close = () => { setIssued(null); setReason(''); issue.reset(); approve.reset(); revoke.reset(); onClose() }
    const failure = issue.error ?? approve.error ?? revoke.error
    const isSelf = userId === myId

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) close() }}>
            <DialogContent className="sm:max-w-xl" data-testid="enrolment-code-dialog">
                <DialogHeader>
                    <DialogTitle>{t('enrolment.admin.title', { name: user?.name || user?.email || '' })}</DialogTitle>
                    <DialogDescription>{t('enrolment.admin.description')}</DialogDescription>
                </DialogHeader>

                {issued && (
                    <div className="space-y-2 rounded-md border border-primary/40 bg-primary/5 p-3" data-testid="issued-code">
                        <p className="text-sm font-medium">{t('enrolment.admin.issuedTitle')}</p>
                        <p className="select-all text-center font-mono text-2xl tracking-widest" data-testid="issued-code-value">{issued.code}</p>
                        <p className="text-xs text-muted-foreground">{t('enrolment.admin.issuedOnce')}</p>
                        {issued.record.status === 'pending_approval' && (
                            <p className="text-xs font-medium text-destructive" data-testid="issued-pending">{t('enrolment.admin.issuedPending')}</p>
                        )}
                    </div>
                )}

                {mayIssue && !isSelf && (
                    <form
                        className="space-y-2"
                        data-testid="issue-form"
                        onSubmit={(e) => { e.preventDefault(); if (reason.trim().length >= 10) { setIssued(null); issue.mutate() } }}
                    >
                        <Label htmlFor="enrolment-reason">{t('enrolment.admin.reasonLabel')}</Label>
                        <div className="flex gap-2">
                            <Input id="enrolment-reason" value={reason} maxLength={255} onChange={(e) => setReason(e.target.value)} />
                            <Button type="submit" disabled={issue.isPending || reason.trim().length < 10}>{t('enrolment.admin.issue')}</Button>
                        </div>
                        <p className="text-xs text-muted-foreground">{t('enrolment.admin.issueHint')}</p>
                    </form>
                )}
                {isSelf && <p className="text-sm text-muted-foreground" data-testid="own-account">{t('enrolment.admin.ownAccount')}</p>}
                {failure && <p className="text-sm text-destructive" role="alert">{handleApiError(failure)}</p>}

                {codes.isLoading && <p className="text-sm text-muted-foreground">{t('devices.loading')}</p>}
                {codes.isError && <p className="text-sm text-destructive" role="alert">{handleApiError(codes.error)}</p>}
                {codes.data && codes.data.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-codes">{t('enrolment.admin.none')}</p>}
                {codes.data && codes.data.length > 0 && (
                    <ul className="max-h-[40vh] space-y-2 overflow-y-auto">
                        {codes.data.map((code) => {
                            const waiting = code.status === 'pending_approval'
                            const liveOrWaiting = waiting || code.status === 'active'
                            // The second administrator is somebody else: not the issuer, not the user.
                            const iMayApprove = mayApprove && waiting && code.issuedBy !== myId && code.userId !== myId
                            return (
                                <li key={code.id} className="rounded-md border bg-card p-3 text-sm" data-testid="enrolment-code" data-status={code.status}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-medium">{t(`enrolment.status.${code.status}`)} · {t(`enrolment.purpose.${code.purpose}`)}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {t('enrolment.admin.issuedBy', { name: code.issuedByUser?.name || code.issuedByUser?.email || 'VaultSentinel' })}
                                                {' · '}<DateTime date={code.issuedAt} />
                                            </div>
                                            {code.reason && <div className="text-xs text-muted-foreground">{code.reason}</div>}
                                            {liveOrWaiting && <div className="text-xs text-muted-foreground">{t('enrolment.admin.expires')} <DateTime date={code.expiresAt} /></div>}
                                            {code.approvedByUser && <div className="text-xs text-muted-foreground">{t('enrolment.admin.approvedBy', { name: code.approvedByUser.name || code.approvedByUser.email })}</div>}
                                            {waiting && !iMayApprove && <div className="text-xs text-destructive">{t('enrolment.admin.waitingForOther')}</div>}
                                        </div>
                                        <div className="flex shrink-0 gap-2">
                                            {iMayApprove && (
                                                <Button size="sm" onClick={() => approve.mutate(code.id)} disabled={approve.isPending}>{t('enrolment.admin.approve')}</Button>
                                            )}
                                            {mayIssue && liveOrWaiting && (
                                                <Button size="sm" variant="outline" onClick={() => revoke.mutate(code.id)} disabled={revoke.isPending}>{t('enrolment.admin.revoke')}</Button>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                )}
            </DialogContent>
        </Dialog>
    )
}
